import { Button, Headline, Text } from '@velocity/ui'
import * as React from 'react'

import { useContent } from '@ngb-frontend/shared/context'

import { useDriverNotificationViewStyles } from './DriverNotificationView.styled'

import type { NotificationViewProps } from '../NotificationView/NotificationView'

export interface DriverNotificationViewProps extends NotificationViewProps {
  submitDisabled?: boolean
}

/**
 * The driver flow's look of a `NotificationView`: no card, a big pictogram,
 * the headline, an optional line and the actions stacked in a narrow column.
 */
export const DriverNotificationView: React.FC<DriverNotificationViewProps> = ({
  onCancel,
  onSubmit,
  headline,
  submitButtonText,
  cancelButtonText,
  children,
  Icon,
  submitButtonVariant = 'primary-filled',
  cancelButtonVariant = 'secondary-outlined',
  submitDisabled,
}) => {
  const classes = useDriverNotificationViewStyles()
  const c = useContent()

  return (
    <div className={classes.root} data-e2e-component="driver-notification-view">
      {Icon && (
        <Icon
          size="large"
          className={classes.pictogram}
          data-e2e-component="pictogram"
        />
      )}
      {headline && (
        <Headline
          variant="400"
          component="h1"
          className={classes.headline}
          data-e2e-component="headline"
        >
          {headline}
        </Headline>
      )}
      {children && (
        <Text
          variant="200"
          className={classes.content}
          data-e2e-component="content"
        >
          {children}
        </Text>
      )}
      {(onSubmit || onCancel) && (
        <div className={classes.actions}>
          {onSubmit && (
            <Button
              variant={submitButtonVariant}
              onClick={onSubmit}
              disabled={submitDisabled}
              data-e2e-component="submit-button"
              fullWidth
            >
              {submitButtonText || c('notifications.shared.submit')}
            </Button>
          )}
          {onCancel && (
            <Button
              variant={cancelButtonVariant}
              onClick={onCancel}
              data-e2e-component="cancel-button"
              fullWidth
            >
              {cancelButtonText || c('notifications.shared.cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
